<template>
    <div>
        <div style="display: flex;">
            <div style="flex: 0 0 10%;" class="hidden-xs">
                <l-profile-picture :user="reviewCycle.revieweeUser" size="m" />
            </div>

            <div style="flex: 0 0 90%;">
                <div class="headline">
                    {{ $t(`reviews.update.nPeersTitle`) }}
                </div>

                <div class="material-tabs hidden-xs">
                    <a class="clickable" tabindex="0" @click="goBack()" @keyup.enter="goBack()">
                        <i class="fal fa-caret-left tw-mr-2"></i>
                        {{ $t(`reviews.update.menuBack`) }}
                    </a>
                    <a class="descriptive">
                        {{
                            $t(`reviews.update.nPeersSubtitle`, {
                                firstname: reviewCycle.revieweeUser.firstname,
                                lastname: reviewCycle.revieweeUser.lastname,
                            })
                        }}
                    </a>
                </div>
            </div>
        </div>

        <p align="justify">
            <span v-if="role === 'reviewee' && reviewCycle.managerReviewsEnabled">
                <l-text
                    :text="
                        $t(`reviews.update.nPeersRevieweeNominate`, {
                            firstname: reviewCycle.managerUser.firstname,
                            lastname: reviewCycle.managerUser.lastname,
                            peerMin: reviewCycle.peerMin,
                            peerMax: reviewCycle.peerMax,
                        })
                    "
                />
            </span>
            <span v-if="role === 'reviewee' && !reviewCycle.managerReviewsEnabled">
                {{
                    $t(`reviews.update.nPeersRevieweeApprove`, {
                        firstname: reviewCycle.revieweeUser.firstname,
                        lastname: reviewCycle.revieweeUser.lastname,
                        peerMin: reviewCycle.peerMin,
                        peerMax: reviewCycle.peerMax,
                    })
                }}
            </span>
            <span v-if="role === 'manager'">
                {{
                    $t(`reviews.update.nPeersManagerApprove`, {
                        firstname: reviewCycle.revieweeUser.firstname,
                        lastname: reviewCycle.revieweeUser.lastname,
                        peerMin: reviewCycle.peerMin,
                        peerMax: reviewCycle.peerMax,
                    })
                }}
            </span>
            <span>
                {{ $t(`reviews.update.nPeersModify`) }}
            </span>
            <span v-if="reviewCycle.directReportReviewsEnabled">
                <l-text :text="$t(`reviews.update.nPeersDirectReportsNominated`)" />
            </span>
        </p>

        <l-space :size="1" />

        <div
            v-for="(peer, index) in reviewCycle.contributors.peers"
            :key="peer._id"
            class="panel panel-default"
        >
            <div class="panel-heading">
                <div class="row">
                    <div class="col-md-4">
                        {{ peer.user.fullname || $t(`reviews.update.nPeersInvitedUser`) }}
                    </div>
                    <div class="col-md-8">
                        <span
                            class="pull-right color-primary clickable"
                            tabindex="0"
                            @click="removePeer(index)"
                            @keyup.enter="removePeer(index)"
                        >
                            <i class="fal fa-trash"></i>
                        </span>
                    </div>
                </div>
            </div>
        </div>

        <div class="row">
            <div class="col-md-6" style="margin-top: -10px;">
                <l-input-typeahead
                    :label="``"
                    :placeholder="$t(`reviews.update.nPeersSearchPlaceholder`)"
                    :data="users"
                    :on-select="addPeer"
                    :on-input-change="typeaheadPeerNominationOnInput"
                />
            </div>
            <div class="col-md-6">
                <l-select
                    value=""
                    :options="optionsPeerNomination"
                    @change="e => addTeam(e.target.value)"
                />
            </div>
        </div>

        <!-- peerMessage (only show if no approval by manager required) -->

        <div
            v-if="reviewCycle.peerNominationsEnabled && !reviewCycle.peerApprovalEnabled"
            class="form-group"
            style="margin-top: 10px"
        >
            <l-input-text
                v-model="peerMessage"
                type="textarea"
                :label="``"
                :placeholder="$t(`reviews.update.placeholderPeerMessage`)"
            />
        </div>

        <!-- End peerMessage -->

        <br />

        <div v-if="error" id="error" class="alert alert-danger">
            <span>{{ error.message }}</span>
        </div>

        <div v-show="peersUpdated" class="alert alert-success">
            <span v-show="reviewCycle.peerApprovalEnabled && role === 'reviewee'">
                {{ $t(`reviews.update.labelPeersNominatedNotApproved`) }}
            </span>
            <span
                v-show="!reviewCycle.peerApprovalEnabled && role === 'reviewee'"
                v-b-popover.hover.top="{
                    content: $t(`reviews.update.labelPeersNominatedHelp`),
                }"
            >
                {{ $t(`reviews.update.labelPeersNominated`) }}
            </span>
            <span v-show="reviewCycle.peerApprovalEnabled && role === 'manager'">
                {{ $t(`reviews.update.labelPeersApproved`) }}
            </span>
            <a href="#/reviews" class="pull-right btn-fake">
                <strong> {{ $t(`reviews.update.btnPeersBack`) }}</strong>
            </a>
        </div>

        <div v-show="!peersUpdated" class="pull-right">
            <button class="btn btn-primary btn-raised" type="button" @click="updatePeers()">
                {{ $t(`reviews.update.btnPeersConfirm`) }}
            </button>
        </div>
        <br />
    </div>
</template>

<script>
import { mapGetters } from "vuex";

import format from "@/utils/format-typeahead";

import LInputText from "@/components/LInputText";
import LInputTypeahead from "@/components/LInputTypeahead";
import LProfilePicture from "@/components/LProfilePicture";
import LSelect from "@/components/LSelect";
import LSpace from "@/components/LSpace";
import LText from "@/components/LText";

export default {
    components: {
        LInputText,
        LInputTypeahead,
        LProfilePicture,
        LSelect,
        LSpace,
        LText,
    },
    props: {
        reviewCycleId: {
            type: String,
            required: true,
        },
        role: {
            type: String,
            required: true,
        },
        viewAs: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            reviewCycle: {},

            peerMessage: "",
            peersUpdated: false,

            error: null,
        };
    },
    computed: {
        ...mapGetters({
            user: "users/current/user",
            getReviewCycle: "reviews/participants/details",
            localTeams: "teams/local/listSortedByName",
            usersSearchList: "users/search/list",
            getUserList: "users/list/list",
        }),

        users() {
            let list = this.usersSearchList
                // Filter current user
                .filter(user => user._id !== this.user._id)
                // Filter already selected peers
                .filter(
                    user =>
                        !this.reviewCycle.contributors?.peers?.find?.(
                            peer => peer.user?._id === user._id,
                        ),
                );

            if (this.reviewCycle?.revieweeUser?._id) {
                list = list.filter(user => this.reviewCycle.revieweeUser._id !== user._id);
            }

            if (
                this.viewAs === "reviewee" &&
                this.reviewCycle.managerReviewsEnabled &&
                this.reviewCycle.managerUser
            ) {
                list = list.filter(user => user._id !== this.reviewCycle.managerUser._id);
            }

            return list.map(format.users);
        },

        optionsPeerNomination() {
            let options = [
                {
                    value: "",
                    text: this.$t(`reviews.update.nPeersSelectTeam`),
                    disabled: true,
                },
            ];

            if (!this.reviewCycle.directReportReviewsEnabled) {
                options.push({
                    value: "reports",
                    text: this.$t(`reviews.update.nPeersSelectReports`),
                });
            }

            this.localTeams.forEach(localTeam => {
                options.push({
                    value: localTeam._id,
                    text: localTeam.name,
                });
            });

            return options;
        },
    },
    watch: {
        reviewCycleId: {
            immediate: true,
            handler(reviewCycleId) {
                this.reviewCycle = this.getReviewCycle(reviewCycleId);
            },
        },
    },
    methods: {
        goBack() {
            this.$router.go(-1);
        },

        removePeer(index) {
            this.reviewCycle.contributors?.peers?.splice?.(index, 1);
        },

        addPeer(user) {
            // check whether already present

            if (this.reviewCycle.contributors?.peers?.find?.(peer => peer.user?._id === user._id))
                return;

            // reviewee shouldn't be able to add manager as peer (if manager reviews are enabled)

            if (
                this.viewAs === "reviewee" &&
                this.reviewCycle.managerReviewsEnabled &&
                this.reviewCycle.managerUser &&
                user &&
                user._id === this.reviewCycle.managerUser._id
            )
                return;

            // it shouldn't be possible to add the reviewee user as peer

            if (this.reviewCycle.revieweeUser._id === user._id) return;

            // manager shouldn't be able to add himself as peer (if manager reviews enabled)

            if (
                this.viewAs === "manager" &&
                this.reviewCycle.managerReviewsEnabled &&
                user &&
                user._id === this.user._id
            )
                return;

            // add to peer array

            this.reviewCycle.contributors?.peers?.push?.({
                user: {
                    _id: user._id,
                    firstname: user.firstname,
                    lastname: user.lastname,
                    fullname: user.fullname,
                },
            });

            this.$store.dispatch("users/search/reset");
        },
        typeaheadPeerNominationOnInput(query) {
            if (query.length >= 2) {
                this.$store.dispatch("users/search/findByName", { query });
            } else {
                this.$store.dispatch("users/search/reset");
            }
        },

        async addTeam(selectedTeam) {
            if (!selectedTeam) return;

            // reset error

            this.error = null;

            // add team

            var filter;

            if (selectedTeam === "reports") {
                filter = { admin: [{ _id: "reportsSearch" }] };
            } else {
                filter = { localTeams: [{ _id: selectedTeam }] };
            }

            await this.$store.dispatch({
                type: "users/list/get",
                payload: { userFilter: { include: filter } },
            });

            const users = this.getUserList();
            users.forEach(user => this.addPeer(user));
        },

        updatePeers() {
            this.error = null;
            this.peersUpdated = false;

            this.$store
                .dispatch({
                    type: "reviews/participants/updatePeers",
                    payload: {
                        reviewee: this.reviewCycle.revieweeUser._id,
                        role: this.role,
                        message: this.peerMessage,
                        reviewCycle: this.reviewCycle._id,
                        managerReviewsEnabled: this.reviewCycle.managerReviewsEnabled,
                        peerApprovalEnabled: this.reviewCycle.peerApprovalEnabled,
                        peers: this.reviewCycle.contributors.peers,
                    },
                })
                .then(() => {
                    this.peersUpdated = true;
                })
                .catch(err => {
                    if (err?.response?.data?.message) {
                        this.error = { message: err.response.data.message };
                    }
                });
        },
    },
};
</script>
